import { forwardRef, type ReactNode, useState } from 'react';
import {
  Platform,
  Pressable,
  type PressableProps,
  type StyleProp,
  StyleSheet,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { useFocusVisible } from '../../hooks/useFocusVisible';
import { useHoverCapability } from '../../hooks/useHoverCapability';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { shadows } from '../../theme/shadows';
import { cardSurface } from '../../theme/surfaces';
import { type RadiusKey, tokens } from '../../theme/tokens';
import { BentoSurface } from './BentoSurface';

type CardVariant = 'default' | 'elevated' | 'accent';
type CardAs = 'view' | 'pressable';

interface BaseCardProps {
  children: ReactNode;
  variant?: CardVariant;
  padding?: keyof typeof tokens.spacing;
  radius?: RadiusKey;
  /**
   * Whether to clip children that overflow the card boundary.
   * Defaults to `false` so absolutely-positioned descendants (e.g. Select dropdown
   * menus) are not clipped by the card's stacking context.
   * Pass `clipped` (or `clipped={true}`) when the card's border-radius must crop
   * child content such as images or progress bars that extend to the card edge.
   */
  clipped?: boolean;
  /**
   * Lift and warm the border on pointer hover. On by default for pressable
   * cards, off for static ones — a card that does not respond to a click has
   * no business responding to a cursor.
   */
  hoverable?: boolean;
  style?: StyleProp<ViewStyle>;
  as?: CardAs;
}

type CardViewProps = BaseCardProps & {
  as?: 'view';
} & Omit<ViewProps, 'style' | 'children'>;

type CardPressableProps = BaseCardProps & {
  as: 'pressable';
} & Omit<PressableProps, 'style' | 'children'>;

export type CardProps = CardViewProps | CardPressableProps;

export const Card = forwardRef<View, CardProps>(function Card(props, ref) {
  const {
    children,
    variant = 'default',
    padding = 'lg',
    radius = 'xl',
    clipped = false,
    hoverable,
    style,
    as = 'view',
    ...rest
  } = props;

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // The ring follows the input modality, as CSS :focus-visible does — a
  // pointer press should not outline what it just pressed, and a keyboard
  // user must still see where they are.
  const focusRingVisible = useFocusVisible();
  const canHover = useHoverCapability();
  const reducedMotion = useReducedMotion();

  const respondsToHover = canHover && (hoverable ?? as === 'pressable');
  const surfaceRadius = tokens.radius[radius];

  const composed: StyleProp<ViewStyle> = [
    styles.base,
    clipped && styles.clipped,
    cardSurface({ hovered: respondsToHover && hovered, selected: variant === 'accent' }),
    {
      padding: tokens.spacing[padding],
      borderRadius: surfaceRadius,
    },
    variant === 'elevated' && (shadows.cardAmbient as ViewStyle),
    style,
  ];

  const hoverHandlers = respondsToHover
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};

  if (as === 'pressable') {
    const { onBlur, onFocus, onHoverIn, onHoverOut, ...pressableProps } = rest as PressableProps;
    return (
      <Pressable
        ref={ref}
        {...pressableProps}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onFocus={(event) => {
          if (Platform.OS === 'web') setFocused(true);
          onFocus?.(event);
        }}
        onHoverIn={(event) => {
          if (respondsToHover) setHovered(true);
          onHoverIn?.(event);
        }}
        onHoverOut={(event) => {
          setHovered(false);
          onHoverOut?.(event);
        }}
        style={({ pressed }) => [
          composed,
          pressed && (reducedMotion ? styles.pressedReducedMotion : styles.pressed),
          focused && focusRingVisible && styles.focusVisible,
        ]}
      >
        <BentoSurface radius={surfaceRadius} />
        {children}
      </Pressable>
    );
  }

  return (
    <View ref={ref} {...(rest as ViewProps)} {...hoverHandlers} style={composed}>
      <BentoSurface radius={surfaceRadius} />
      {children}
    </View>
  );
});

const styles = StyleSheet.create({
  base:
    Platform.select({
      default: { overflow: 'hidden' } as ViewStyle,
      web: { overflow: 'visible' } as ViewStyle,
    }) ?? {},
  clipped: {
    overflow: 'hidden',
  },
  pressed: {
    opacity: 0.92,
    transform: [{ scale: 0.995 }],
  },
  pressedReducedMotion: {
    opacity: 0.92,
  },
  focusVisible:
    Platform.select({
      web: {
        outlineColor: colors.border.focus,
        outlineOffset: tokens.interaction.focusRing.offset,
        outlineStyle: 'solid',
        outlineWidth: tokens.interaction.focusRing.width,
      } as unknown as ViewStyle,
      default: {},
    }) ?? {},
});
